// Mantine
import {
    Anchor,
    Divider,
    Badge,
    Modal,
    ActionIcon,
    Tooltip,
    Switch,
    Menu,
} from '@mantine/core'

// Hooks
import useUserLocation from '@/hooks/useUserLocation'
import { useLocalStorage } from '@mantine/hooks'
import { useComponentsAvailability } from '@/hooks/useComponentsAvailability'

import {
    IconX,
    IconDotsVertical,
    IconExternalLink,
    IconPrinter,
} from '@tabler/icons-react'

// Dayjs
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

// UI
import FulfillmentOrderEdit from './FulfillmentOrderEdit'
import Banner from '../ui/Banner'
import { isOrderSettled } from '../orders/OrderStatusBadges'

// Motifs d'annulation proposés au client sur la page de suivi. Miroir de
// OrderManagementController::CANCEL_REASONS — la valeur brute s'affiche si
// un motif y est ajouté sans passer ici.
import { CANCELLATION_REASONS } from '@/lib/cancellationReasons'

import OrderWarrantiesDisplay from '../orders/OrderWarrantiesDisplay'
import LineItemsList from '../ui/LineItemsList'
import FulfillmentSmallDisplay from '../fulfillments/FulfillmentSmallDisplay'
import FulfillmentOrdersShipments from './FulfillmentOrdersShipments'
import ShipmentPrintModal from '../shipments/ShipmentPrintModal'

import FulfillmentOrderOptions from './FulfillmentOrderOptions'
import Box from '../ui/Box'
import ComponentsAvailabilityChecker from '../ui/ComponentsAvailabilityChecker'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

import { useState } from 'react'

// Bus
import { useListener } from 'react-bus'

import { BACKOFFICE_API_URL } from '@/lib/constants'

export default function FulfillmentOrderDisplay({
    fulfillment_order,
    loadComponent,
}) {
    const [warrantyCodeToAssociate, setWarrantyCodeToAssociate] = useState(null)

    // Get lines with remaining quantity
    const lines = fulfillment_order.lines
        .filter(
            (line) =>
                line.remaining_quantity > 0 && line.requires_shipping === true
        )
        .sort((a, b) => a.id - b.id) // Sort lines by id

    const warranties = [...fulfillment_order.warranties]
    const items = lines.flatMap((line) => {
        const watchHeadId = line.shopify_product_variant?.components?.find(
            (component) => component.category === 'watch_head'
        )?.id

        return Array.from({ length: line.remaining_quantity }, () => {
            // Allocate each warranty to one unit of its variant, even when
            // several variants share the same watch head.
            const warrantyIndex = watchHeadId
                ? warranties.findIndex(
                      (warranty) =>
                          warranty.component_id === watchHeadId &&
                          warranty.shopify_product_variant_id ===
                              line.shopify_product_variant.id
                  )
                : -1
            const warranty =
                warrantyIndex >= 0
                    ? warranties.splice(warrantyIndex, 1)[0]
                    : undefined

            return { ...line, watchHeadId, warranty }
        })
    })

    const consignee =
        fulfillment_order.consignee ?? fulfillment_order.order?.shipping_address

    const consigneeEmail = consignee?.email ?? fulfillment_order.order?.email

    // For inter-location transfers, to_location is the destination
    const isToLocationComplete =
        fulfillment_order.to_location_id &&
        fulfillment_order.to_location?.address1 &&
        fulfillment_order.to_location?.city &&
        fulfillment_order.to_location?.country_code

    const isConsigneeAdressComplete =
        isToLocationComplete ||
        ((consignee?.name || consignee?.company) &&
            consignee?.address1 &&
            consignee?.city &&
            consignee?.country_code)

    const isOpen = ['OPEN', 'IN_PROGRESS'].includes(fulfillment_order.status)

    // Disponibilité des composants (SWR déduplique avec ComponentsAvailabilityChecker)
    const {
        hasVariantPhysicalStock,
        getOutOfStockComponentsForVariant,
        getComponentPhysicalStatus,
        result: availabilityResult,
    } = useComponentsAvailability({
        locationId: fulfillment_order.location_id,
        lines: isOpen
            ? lines.map((line) => ({
                  variant_id: line.variant_id,
                  component_id: line.component_id,
                  quantity: line.remaining_quantity,
              }))
            : [],
    })

    // Check if the precise location of the user is the same as the location of the fulfillment order
    const { userLocationId, userPrinter, userPrinters } = useUserLocation()
    const a4Printer = userPrinters.a4
    const productLabelPrinter = userPrinters.product_label

    const isAtLocation = userLocationId === fulfillment_order.location_id

    // Le client a demandé l'annulation et personne n'a tranché : on ne touche
    // à rien. Le drapeau sort déjà la préparation de la file d'attente
    // (FulfillmentOrder::scopeToBeFulfilled) — ici il verrouille le dossier
    // pour celui qui l'ouvre quand même.
    const cancellationRequested = Boolean(
        fulfillment_order.order?.cancellation_requested_at &&
            !fulfillment_order.order?.cancellation_resolved_at &&
            !isOrderSettled(fulfillment_order.order)
    )

    // Check if the order is risky
    const isRisky =
        fulfillment_order.order &&
        fulfillment_order.order.tags.includes('risky')

    // US customs blocked
    const isUSCustomsBlocked =
        fulfillment_order.order &&
        fulfillment_order.order.tags.includes('US_customs')

    // Check if there is something to pay and that it has to be paid before fulfilling the order
    const waitingForPayment =
        fulfillment_order.order?.total_outstanding > 0 &&
        !['FULFILLMENT', 'FIXED', 'NET'].includes(
            fulfillment_order.order?.payment_terms_type
        )

    // Check if the order can be fulfilled
    const canBeFulfilled =
        isOpen &&
        isConsigneeAdressComplete &&
        !waitingForPayment &&
        !cancellationRequested &&
        !isRisky &&
        !isUSCustomsBlocked &&
        items.length > 0 &&
        isAtLocation

    const mustBeFulfilled =
        canBeFulfilled &&
        dayjs()
            .format('YYYY-MM-DD')
            .localeCompare(fulfillment_order.fulfill_at) >= 0

    const canBeMarkedAsFullfilled =
        isOpen &&
        !waitingForPayment &&
        !cancellationRequested &&
        !isRisky &&
        items.length > 0 &&
        isAtLocation

    // Watch Clock Details
    const hasWatchClockDetails = lines.some(
        (line) =>
            line.shopify_product_variant?.wcd_jewels && line.unit_price_initial
    )

    // Associate a warranty
    const { requestPost } = useAxiosApi()

    const printWatchWorksheet = async () => {
        await requestPost(
            `/fulfillment-orders/${fulfillment_order.id}/print-watch-worksheet`,
            {
                printnode_ids: a4Printer.printnode_ids,
            }
        )
    }

    const associateWarranty = async ({
        shopify_product_variant_id,
        component_id,
        code,
    }) => {
        try {
            await requestPost('/warranty/associate', {
                shopify_product_variant_id,
                component_id,
                codes: [code],
                warranty_start_at: dayjs().format('YYYY-MM-DD'),
                order_id: fulfillment_order.order?.id,
                fulfillment_order_id: fulfillment_order.id,
            })

            await loadComponent()

            setWarrantyCodeToAssociate(null)
        } catch {}
    }

    // Delete a warranty
    const { requestDelete, requestPatch } = useAxiosApi()

    const [isDeleting, setIsDeleting] = useState(false)

    const deleteWarranty = async (code) => {
        setIsDeleting(true)

        await requestDelete(`/warranties/${code}`)
        await loadComponent()

        setIsDeleting(false)
    }

    // Mark line as ready
    const printVariantLabel = async (line) => {
        const ids = Array(line.remaining_quantity)
            .fill(String(line.variant_id))
            .join(',')
        await requestPost('/shopify-variants/print-labels', {
            variant_ids: ids,
            printnode_ids: productLabelPrinter.printnode_ids,
        })
    }

    // Mark line as ready
    const markLineAsReady = async (line) => {
        await requestPost('/fulfillment-order-line-items/' + line.id + '/ready')
        await loadComponent()
    }

    const markLineAsNotReady = async (line) => {
        await requestPost(
            '/fulfillment-order-line-items/' + line.id + '/not-ready'
        )
        await loadComponent()
    }

    // Check if it has a shipment label
    const shipmentLabel = fulfillment_order.shipments.find(
        (s) => s.canceled_at === null
    )

    // Listen to scan event
    useListener('scan', (scan) => {
        if (
            scan?.type === 'warranty' &&
            fulfillment_order.warranties_mode === 'associate'
        ) {
            // Get lines that can be associated to the warranty
            const linesThatCanBeAssociated = items.filter(
                (item) => item.watchHeadId && !item.warranty
            )

            if (linesThatCanBeAssociated.length === 1) {
                const item = linesThatCanBeAssociated[0]

                associateWarranty({
                    shopify_product_variant_id: item.shopify_product_variant.id,
                    component_id: item.watchHeadId,
                    code: scan.code,
                })
            } else if (linesThatCanBeAssociated.length > 1) {
                // If there are multiple lines, ask the user to select the line to associate the warranty to
                setWarrantyCodeToAssociate(scan.code)
            } else {
                setWarrantyCodeToAssociate(null)
            }
        }
    })

    // Print and mark as fullfilled
    const { requestPost: requestPost2 } = useAxiosApi()

    const [isMarkingAsFullfilled, setIsMarkingAsFullfilled] = useState(false)

    const printAndMarkAsFullfilled = async () => {
        // Get printnode ids
        const printnode_ids = userPrinter?.printnode_ids

        if (!printnode_ids) {
            return
        }

        setIsMarkingAsFullfilled(true)

        await requestPost2(
            '/fulfillment-orders/' + fulfillment_order.id + '/print-and-send',
            {
                printnode_ids,
            }
        )

        await loadComponent()

        setIsMarkingAsFullfilled(false)
    }

    // Only print
    const [isPrinting, setIsPrinting] = useState(false)

    const printOnly = async () => {
        // Get printnode ids
        const printnode_ids = userPrinter?.printnode_ids

        if (!printnode_ids) {
            return
        }

        setIsPrinting(true)

        await requestPost(
            '/fulfillment-orders/' + fulfillment_order.id + '/print',
            {
                printnode_ids,
            }
        )

        setIsPrinting(false)
    }

    // Only mark as fullfilled
    const sendOnly = async ({ tracking_code } = {}) => {
        const payload = {
            ...(tracking_code && { tracking_code }),
        }

        await requestPost(
            '/fulfillment-orders/' + fulfillment_order.id + '/send',
            payload
        )

        await loadComponent()
    }

    // Is being prepared
    const updateIsBeingPrepared = async (isBeingPrepared) => {
        await requestPatch(`/fulfillment-orders/${fulfillment_order.id}`, {
            is_being_prepared: isBeingPrepared,
        })

        await loadComponent()
    }

    const multiItemsDisplayProps = {
        lines,
        warranties: fulfillment_order.warranties,
        warrantyCodeToAssociate,
        associateWarranty,
        loadComponent,
        fulfillment_order,
        markLineAsReady,
        markLineAsNotReady,
        onPrintLabel: productLabelPrinter ? printVariantLabel : null,
        hasVariantPhysicalStock,
        getOutOfStockComponentsForVariant,
        getComponentPhysicalStatus,
        availabilityResult,
    }

    // Count how many remaining quantities need a warranty
    let warrantiesNeeded = 0

    lines.forEach((line) => {
        if (
            line?.shopify_product_variant?.components.find(
                (c) => c.category === 'watch_head'
            )?.id
        ) {
            warrantiesNeeded += line.remaining_quantity
        }
    })

    return (
        <div className="mx-auto grid grid-cols-1 @5xl:grid-cols-[2fr_1fr] gap-5 w-full max-w-[1200px]">
            <div className="flex flex-col gap-5 min-w-0">
                {fulfillment_order.status === 'CLOSED' && (
                    <Banner
                        type="success"
                        title="Demande de préparation fermée"
                    >
                        La demande n'est pas à traiter.
                    </Banner>
                )}

                {fulfillment_order.status === 'CANCELLED' && (
                    <Banner type="error" title="Demande de préparation annulée">
                        <span>
                            Cette demande de préparation ne doit pas être
                            traitée.
                        </span>
                    </Banner>
                )}

                {fulfillment_order.fulfillments.map((fulfillment) => (
                    <FulfillmentSmallDisplay
                        fulfillment={fulfillment}
                        key={fulfillment.id}
                    />
                ))}

                {lines.length > 0 && isOpen && (
                    <Box
                        title="Produits à préparer"
                        rightSection={
                            <div className="flex items-center gap-2">
                                <Switch
                                    label="En cours de préparation"
                                    labelPosition="left"
                                    checked={
                                        fulfillment_order.is_being_prepared
                                    }
                                    onChange={(e) =>
                                        updateIsBeingPrepared(
                                            e.currentTarget.checked
                                        )
                                    }
                                />
                                {hasWatchClockDetails && shipmentLabel && (
                                    <Menu shadow="md" position="bottom-end">
                                        <Menu.Target>
                                            <ActionIcon variant="default">
                                                <IconDotsVertical size={16} />
                                            </ActionIcon>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Item
                                                component="a"
                                                target="_blank"
                                                href={`${BACKOFFICE_API_URL}/fulfillment-orders/${fulfillment_order.id}/watch-worksheet.pdf`}
                                                leftSection={
                                                    <IconExternalLink
                                                        size={16}
                                                    />
                                                }
                                            >
                                                Watch Clock Details (PDF)
                                            </Menu.Item>
                                            {a4Printer && (
                                                <Menu.Item
                                                    leftSection={
                                                        <IconPrinter
                                                            size={16}
                                                        />
                                                    }
                                                    onClick={
                                                        printWatchWorksheet
                                                    }
                                                >
                                                    Imprimer WCD sur{' '}
                                                    {a4Printer.name}
                                                </Menu.Item>
                                            )}
                                        </Menu.Dropdown>
                                    </Menu>
                                )}
                            </div>
                        }
                    >
                        <div className="hidden bg-red-50 flex flex-col gap-3 mt-5">
                            {lines.map((line, index) => {
                                let action = null
                                const warantiesForThisProduct = []

                                return <div key={index}>Ligne</div>
                            })}
                        </div>

                        <LineItemsList {...multiItemsDisplayProps} />

                        <div className="flex flex-col gap-3 mt-5">
                            {/* Orange et non rouge : rien n'est annulé, la
                                décision reste à prendre. Bloquant malgré la
                                couleur — comme « Paiement en attente », qui est
                                orange et verrouille aussi. */}
                            {cancellationRequested && (
                                <Banner
                                    compact
                                    type="warning"
                                    title="Annulation demandée par le client"
                                >
                                    <div className="flex flex-col gap-1">
                                        <span>
                                            Ne pas préparer tant que la demande
                                            n'a pas été tranchée. Reçue le{' '}
                                            {dayjs(
                                                fulfillment_order.order
                                                    .cancellation_requested_at
                                            ).format('DD/MM/YYYY à HH:mm')}
                                            .
                                        </span>

                                        {fulfillment_order.order
                                            .cancellation_reason && (
                                            <span>
                                                Motif :{' '}
                                                {CANCELLATION_REASONS[
                                                    fulfillment_order.order
                                                        .cancellation_reason
                                                ] ??
                                                    fulfillment_order.order
                                                        .cancellation_reason}
                                            </span>
                                        )}
                                    </div>
                                </Banner>
                            )}

                            {/* Error */}
                            {isRisky && (
                                <Banner
                                    compact
                                    type="error"
                                    title="Impossible d'expédier"
                                >
                                    La commande est marquée comme risquée.
                                </Banner>
                            )}

                            {isUSCustomsBlocked && (
                                <Banner
                                    compact
                                    type="error"
                                    title="Blocage DHL"
                                >
                                    Les expéditions d'un montant supérieur à 800
                                    USD vers les États-Unis sont suspendues à
                                    partir du 21 avril 2025.
                                </Banner>
                            )}

                            {!isAtLocation && (
                                <Banner
                                    compact
                                    type="error"
                                    title="Localisation invalide"
                                >
                                    Pour préparer cette demande, vous devez être
                                    au lieu de préparation souhaité. Modifier
                                    votre lieu actuel ou le lieu de préparation
                                    de la demande de préparation.
                                </Banner>
                            )}

                            {fulfillment_order.verify_address_error && (
                                <Banner
                                    compact
                                    type="error"
                                    title="Adresse invalide"
                                >
                                    Erreur lors de la vérification de l'adresse
                                    :
                                    <br />
                                    {fulfillment_order.verify_address_error}
                                </Banner>
                            )}

                            {!shipmentLabel && (
                                <Banner
                                    compact
                                    type="error"
                                    title="Bordereau d'expédition manquant"
                                >
                                    Veuillez générer un bordereau d'expédition
                                    pour expédier cette demande de préparation.
                                </Banner>
                            )}

                            {/* Warning */}
                            {waitingForPayment > 0 && (
                                <Banner
                                    compact
                                    type="warning"
                                    title="Paiement en attente"
                                >
                                    Un paiement est en attente pour la commande.
                                </Banner>
                            )}

                            {fulfillment_order.order?.total_outstanding < 0 && (
                                <Banner
                                    compact
                                    type="warning"
                                    title="Remboursement en attente"
                                >
                                    Un remboursement est en attente pour la
                                    commande.
                                </Banner>
                            )}

                            {fulfillment_order.warranties_mode === 'none' && (
                                <Banner
                                    compact
                                    type="warning"
                                    title="Pas de cartes de garanties"
                                >
                                    Ne pas ajouter de cartes de garanties
                                </Banner>
                            )}

                            {/* Stock */}
                            <ComponentsAvailabilityChecker
                                locationId={fulfillment_order.location_id}
                                lines={lines.map((line) => ({
                                    variant_id: line.variant_id,
                                    component_id: line.component_id,
                                    quantity: line.remaining_quantity,
                                }))}
                                mode="physical"
                            />

                            {canBeFulfilled && !mustBeFulfilled && (
                                <Banner
                                    compact
                                    type="info"
                                    title="Date de préparation"
                                >
                                    {fulfillment_order.fulfill_at === null && (
                                        <>Pas de date de préparation définie.</>
                                    )}

                                    {fulfillment_order.fulfill_at !== null && (
                                        <>
                                            La préparation est prévue à partir
                                            du {''}
                                            {dayjs(
                                                fulfillment_order.fulfill_at
                                            ).format('LL')}
                                        </>
                                    )}
                                </Banner>
                            )}
                        </div>

                        <div className="flex gap-3 items-center mt-5 mx-auto justify-center">
                            {canBeFulfilled && shipmentLabel && (
                                <>
                                    <ShipmentPrintModal
                                        shipment={shipmentLabel}
                                        loading={isMarkingAsFullfilled}
                                        printAndMarkAsFullfilled={
                                            printAndMarkAsFullfilled
                                        }
                                    />
                                </>
                            )}

                            {canBeMarkedAsFullfilled && (
                                <FulfillmentOrderOptions
                                    shipment={shipmentLabel}
                                    fulfillment_order={fulfillment_order}
                                    sendOnly={sendOnly}
                                    printOnly={printOnly}
                                />
                            )}
                        </div>
                    </Box>
                )}

                {fulfillment_order.fulfillment_mode === 'shipment' &&
                    fulfillment_order.fulfillments.length === 0 && (
                        <FulfillmentOrdersShipments
                            fulfillment_order={fulfillment_order}
                            loadFullfillmentOrder={loadComponent}
                        />
                    )}

                {(fulfillment_order.warranties.length > 0 ||
                    fulfillment_order.warranties_mode === 'associate') && (
                    <OrderWarrantiesDisplay
                        warranties={fulfillment_order.warranties}
                    />
                )}
            </div>

            <div className="flex flex-col gap-5">
                {fulfillment_order.fulfillments.length === 0 && (
                    <Box
                        title="Informations"
                        rightSection={
                            items.length > 0 && (
                                <FulfillmentOrderEdit
                                    fulfillment_order={fulfillment_order}
                                    loadComponent={loadComponent}
                                />
                            )
                        }
                    >
                        <div className="flex flex-col text-gray-500">
                            <span className="text-base font-medium text-gray-800">
                                {fulfillment_order.location.name}
                            </span>
                            {fulfillment_order.original_fulfill_at &&
                                fulfillment_order.original_fulfill_at !==
                                    fulfillment_order.fulfill_at && (
                                    <span className="text-gray-400 text-sm line-through">
                                        {dayjs(
                                            fulfillment_order.original_fulfill_at
                                        ).format('LL')}
                                    </span>
                                )}
                            <span
                                className={`font-medium ${fulfillment_order.fulfill_at && dayjs(fulfillment_order.fulfill_at).isAfter(dayjs(), 'day') ? 'text-red-500' : 'text-green-500'}`}
                            >
                                {fulfillment_order.fulfill_at
                                    ? dayjs(
                                          fulfillment_order.fulfill_at
                                      ).format('LL')
                                    : 'Pas de date'}
                            </span>
                            <span>
                                {fulfillment_order.warranties_mode === 'blank'
                                    ? 'Garanties vierges'
                                    : fulfillment_order.warranties_mode ===
                                        'associate'
                                      ? 'Associer les garanties'
                                      : 'Pas de garanties'}
                            </span>
                            <span>
                                {fulfillment_order.incoterm === 'DDP'
                                    ? 'Douane prépayée (DDP)'
                                    : 'Douane à la charge du destinataire (DAP)'}
                            </span>
                        </div>
                    </Box>
                )}

                <Box title="Destinataire">
                    {fulfillment_order.to_location_id &&
                        fulfillment_order.to_location && (
                            <div className="flex flex-col">
                                <span className="text-gray-500">
                                    Transfert vers
                                </span>
                                <span className="font-medium">
                                    {fulfillment_order.to_location.name}
                                </span>
                                {fulfillment_order.to_location.address1 && (
                                    <>
                                        <span className="text-gray-700 mt-2">
                                            {
                                                fulfillment_order.to_location
                                                    .address1
                                            }
                                        </span>
                                        <span className="text-gray-700">
                                            {fulfillment_order.to_location.city}{' '}
                                            {fulfillment_order.to_location.zip}
                                        </span>
                                        <span className="text-gray-700">
                                            {
                                                fulfillment_order.to_location
                                                    .country_code
                                            }
                                        </span>
                                    </>
                                )}
                            </div>
                        )}

                    {!fulfillment_order.to_location_id && consignee && (
                        <div className="flex flex-col">
                            <span className="font-medium">
                                {consignee.name}
                            </span>

                            <span>{consigneeEmail}</span>

                            <span>{consignee.phone}</span>

                            <Divider
                                className="w-full"
                                my="sm"
                                variant="dashed"
                            />

                            <span className="text-gray-700">
                                {consignee.first_name} {consignee.last_name}
                            </span>

                            <span className="text-gray-700">
                                {consignee.company}
                            </span>

                            <span className="text-gray-700">
                                {consignee.address1}
                            </span>

                            <span className="text-gray-700">
                                {consignee.city} {consignee.province_code}{' '}
                                {consignee.zip}
                            </span>

                            <span className="text-gray-700">
                                {consignee.country_code}
                            </span>

                            {fulfillment_order.is_address_verified === true &&
                                fulfillment_order.verify_address_error ===
                                    null && (
                                    <div className="mt-2 text-green-500 font-medium">
                                        Adresse vérifiée par DHL
                                    </div>
                                )}
                        </div>
                    )}
                </Box>

                {fulfillment_order.service_case_id && (
                    <Box title="Dossier SAV">
                        <div className="flex flex-col">
                            <Anchor
                                href={`/service-cases/${fulfillment_order.service_case_id}`}
                            >
                                BR
                                {String(
                                    fulfillment_order.service_case_id
                                ).padStart(5, '0')}
                            </Anchor>
                        </div>
                    </Box>
                )}

                {fulfillment_order.retailer_estimate && (
                    <Box title="Devis revendeur">
                        <div className="flex flex-col">
                            <div>
                                <Anchor
                                    href={`/retailer_estimates/${fulfillment_order.retailer_estimate.id}`}
                                >
                                    {fulfillment_order.retailer_estimate.number}
                                </Anchor>{' '}
                                pour{' '}
                                <Anchor
                                    href={`/retailers/${fulfillment_order.retailer_estimate.retailer.id}`}
                                >
                                    {
                                        fulfillment_order.retailer_estimate
                                            .retailer.name
                                    }
                                </Anchor>
                            </div>

                            <span className="text-gray-600">
                                Créé le{' '}
                                {dayjs(
                                    fulfillment_order.retailer_estimate
                                        .created_at
                                ).format('LL [à] HH:mm')}
                            </span>
                        </div>
                    </Box>
                )}

                {fulfillment_order.order && (
                    <Box
                        title="Commande Shopify"
                        rightSection={
                            <Menu shadow="md" position="bottom-end">
                                <Menu.Target>
                                    <ActionIcon variant="default">
                                        <IconDotsVertical size={16} />
                                    </ActionIcon>
                                </Menu.Target>

                                <Menu.Dropdown>
                                    <Menu.Item
                                        component="a"
                                        target="_blank"
                                        href={
                                            'https://admin.shopify.com/store/baltic-shfy/orders/' +
                                            fulfillment_order.order.id
                                        }
                                        leftSection={
                                            <IconExternalLink size={14} />
                                        }
                                    >
                                        Voir sur Shopify
                                    </Menu.Item>
                                    <Menu.Item
                                        component="a"
                                        target="_blank"
                                        href={
                                            'https://baltic-watches.com/order/' +
                                            fulfillment_order.order.id +
                                            '/' +
                                            fulfillment_order.order.order_hash?.slice(
                                                0,
                                                16
                                            )
                                        }
                                        leftSection={
                                            <IconExternalLink size={14} />
                                        }
                                    >
                                        Page de suivi
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        }
                    >
                        <div className="flex flex-col">
                            <span>
                                <Anchor
                                    href={
                                        '/orders/' + fulfillment_order.order.id
                                    }
                                >
                                    {fulfillment_order.order.name}
                                </Anchor>{' '}
                                de {fulfillment_order.order.customer.first_name}{' '}
                                {''}
                                {fulfillment_order.order.customer.last_name}
                            </span>

                            <span className="text-gray-600">
                                {dayjs(
                                    fulfillment_order.order.created_at
                                ).format('LL [à] HH:mm')}
                            </span>

                            {fulfillment_order.order.tags && (
                                <div className="mt-3">
                                    {fulfillment_order.order.tags
                                        .split(',')
                                        .map((tag) => (
                                            <Badge
                                                key={tag}
                                                variant="light"
                                                color="gray"
                                            >
                                                {tag}
                                            </Badge>
                                        ))}
                                </div>
                            )}
                        </div>
                    </Box>
                )}
            </div>
        </div>
    )
}
